import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { Header } from "../components/Header";
import { Footer } from "../components/sections/Footer";
import { CookieBanner, reopenCookieBanner } from "../components/CookieBanner";
import { useLanguage } from "../contexts/LanguageContext";
import { applySEO } from "../lib/seo";

export default function PrivacyPolicy() {
  const { lang, t } = useLanguage();

  useEffect(() => {
    applySEO({
      title:
        lang === "fr"
          ? "Politique de confidentialité — Richild Invest GBC"
          : "Privacy Policy — Richild Invest GBC",
      description:
        lang === "fr"
          ? "Politique de confidentialité et de protection des données personnelles de Richild Invest GBC, conforme au RGPD."
          : "Privacy and personal data protection policy of Richild Invest GBC, compliant with GDPR.",
      url: "https://richildinvest.com/politique-confidentialite",
      type: "article",
      lang,
    });
    window.scrollTo(0, 0);
  }, [lang]);

  const content = lang === "fr" ? frContent : enContent;
  const cookieBtnLabel =
    lang === "fr" ? "Gérer mes préférences de cookies" : "Manage my cookie preferences";

  return (
    <div data-testid="privacy-policy-page" className="bg-white min-h-screen">
      <Header />
      <main className="pt-32 pb-24">
        <div className="max-w-3xl mx-auto px-6 sm:px-8">
          <Link
            to="/"
            data-testid="privacy-back-link"
            className="inline-flex items-center gap-2 text-sm text-[#0D2F4C]/70 hover:text-[#0D2F4C] mb-10 transition-colors"
          >
            <ArrowLeft size={14} />
            {t.legal.back}
          </Link>

          <div className="text-xs uppercase tracking-[0.25em] text-[#C9A961] mb-3">
            Richild Invest GBC
          </div>
          <h1
            className="text-4xl sm:text-5xl text-[#0D2F4C] mb-4"
            style={{ fontFamily: "'Playfair Display', serif" }}
          >
            {t.legal.privacy_title}
          </h1>
          <p className="text-sm text-[#0D2F4C]/60 mb-12 border-b border-[#0D2F4C]/10 pb-8">
            {t.legal.privacy_updated}
          </p>

          <article
            className="legal-content"
            dangerouslySetInnerHTML={{ __html: content }}
          />

          <div className="mt-16 pt-10 border-t border-[#0D2F4C]/10">
            <button
              data-testid="privacy-reopen-cookies-btn"
              onClick={reopenCookieBanner}
              className="bg-[#0D2F4C] text-white px-6 py-3 text-sm tracking-wide hover:bg-[#0A243A] transition-colors"
              style={{ borderRadius: 0 }}
            >
              {cookieBtnLabel}
            </button>
          </div>
        </div>
      </main>
      <Footer />
      <CookieBanner />
    </div>
  );
}

const frContent = `
<p>
  Richild Invest GBC accorde une importance majeure à la protection des données
  personnelles de ses visiteurs, clients et prospects. La présente politique de
  confidentialité décrit comment nous collectons, utilisons et protégeons vos
  données conformément au <strong>Règlement Général sur la Protection des Données
  (RGPD - UE 2016/679)</strong>.
</p>

<h2>1. Responsable du traitement</h2>
<p>
  Le responsable du traitement des données collectées via ce site est :<br>
  <strong>Richild Invest GBC</strong> — Port Louis, Île Maurice<br>
  Contact : <a href="mailto:info@richildinvest.com">info@richildinvest.com</a>
</p>

<h2>2. Données collectées</h2>
<p>Nous collectons les données suivantes :</p>
<ul>
  <li><strong>Formulaire de contact</strong> : nom, e-mail, société, sujet, message.</li>
  <li><strong>Téléchargement de ressources (rapport ESG)</strong> : nom, e-mail, société.</li>
  <li><strong>Données de navigation</strong> (uniquement avec votre consentement) :
    adresse IP anonymisée, pages visitées, durée de visite, type de navigateur, via
    Google Analytics 4.</li>
</ul>

<h2>3. Finalités du traitement</h2>
<ul>
  <li>Répondre à vos demandes de contact et opportunités d'investissement.</li>
  <li>Vous envoyer les ressources demandées (rapports, livres blancs).</li>
  <li>Améliorer la performance et l'ergonomie du site (statistiques anonymes).</li>
  <li>Respecter nos obligations légales et réglementaires.</li>
</ul>

<h2>4. Base légale</h2>
<p>
  Les traitements sont fondés sur :
</p>
<ul>
  <li>Votre <strong>consentement explicite</strong> (cookies analytiques, lead magnet).</li>
  <li>L'<strong>intérêt légitime</strong> de Richild Invest GBC pour répondre à vos demandes.</li>
  <li>L'<strong>exécution de mesures précontractuelles</strong> à votre demande.</li>
</ul>

<h2>5. Durée de conservation</h2>
<ul>
  <li>Formulaires de contact : <strong>3 ans</strong> à compter du dernier échange.</li>
  <li>Leads (téléchargements) : <strong>3 ans</strong> à compter de l'inscription.</li>
  <li>Données analytiques (GA4) : <strong>14 mois</strong> (anonymisées).</li>
</ul>

<h2>6. Destinataires et sous-traitants</h2>
<p>Vos données sont accessibles uniquement aux personnes habilitées au sein de
  Richild Invest GBC et à nos sous-traitants techniques :</p>
<ul>
  <li><strong>MongoDB Atlas</strong> (hébergement de la base de données, UE - Paris)</li>
  <li><strong>Render.com</strong> (hébergement de l'application, UE - Francfort)</li>
  <li><strong>Resend</strong> (service d'envoi d'e-mails transactionnels)</li>
  <li><strong>Google Analytics 4</strong> (mesure d'audience, avec consentement)</li>
  <li><strong>Calendly</strong> (prise de rendez-vous, avec votre interaction)</li>
</ul>
<p>
  Aucune donnée n'est revendue à des tiers à des fins commerciales.
</p>

<h2>7. Transferts hors UE</h2>
<p>
  Certains sous-traitants (Render, Resend, Google) sont situés hors de l'Union
  européenne. Ces transferts sont encadrés par les <strong>clauses contractuelles
  types</strong> de la Commission européenne et/ou par les programmes de certification
  reconnus (Data Privacy Framework).
</p>

<h2>8. Vos droits</h2>
<p>
  Conformément au RGPD, vous disposez des droits suivants :
</p>
<ul>
  <li><strong>Droit d'accès</strong> à vos données personnelles.</li>
  <li><strong>Droit de rectification</strong> en cas d'inexactitude.</li>
  <li><strong>Droit à l'effacement</strong> (« droit à l'oubli »).</li>
  <li><strong>Droit à la limitation</strong> du traitement.</li>
  <li><strong>Droit à la portabilité</strong> de vos données.</li>
  <li><strong>Droit d'opposition</strong> au traitement.</li>
  <li><strong>Droit de retirer votre consentement</strong> à tout moment.</li>
</ul>
<p>
  Pour exercer vos droits, contactez-nous à :
  <a href="mailto:info@richildinvest.com">info@richildinvest.com</a>.
  Vous disposez également du droit d'introduire une réclamation auprès de la CNIL
  (<a href="https://www.cnil.fr" target="_blank" rel="noopener noreferrer">www.cnil.fr</a>).
</p>

<h2>9. Cookies</h2>
<p>
  Notre site utilise uniquement deux types de cookies :
</p>
<ul>
  <li><strong>Cookies essentiels</strong> (préférence de langue, choix RGPD) : nécessaires au
    fonctionnement du site, exemptés de consentement.</li>
  <li><strong>Cookies analytiques (Google Analytics 4)</strong> : déposés uniquement après
    votre consentement explicite via le bandeau cookies. Adresses IP anonymisées.</li>
</ul>
<p>
  Vous pouvez à tout moment modifier vos préférences via le bouton ci-dessous.
</p>

<h2>10. Sécurité</h2>
<p>
  Nous mettons en œuvre toutes les mesures techniques et organisationnelles
  raisonnables pour protéger vos données : chiffrement HTTPS (TLS 1.3),
  authentification forte, accès restreint, sauvegardes régulières.
</p>

<h2>11. Modifications</h2>
<p>
  La présente politique peut être modifiée à tout moment. La version en vigueur
  est toujours celle publiée sur cette page.
</p>
`;

const enContent = `
<p>
  Richild Invest GBC places great importance on the protection of personal data of its
  visitors, clients, and prospects. This privacy policy describes how we collect, use,
  and protect your data in accordance with the <strong>General Data Protection Regulation
  (GDPR - EU 2016/679)</strong>.
</p>

<h2>1. Data controller</h2>
<p>
  The controller of the data collected via this site is:<br>
  <strong>Richild Invest GBC</strong> — Port Louis, Mauritius<br>
  Contact: <a href="mailto:info@richildinvest.com">info@richildinvest.com</a>
</p>

<h2>2. Data collected</h2>
<p>We collect the following data:</p>
<ul>
  <li><strong>Contact form</strong>: name, email, company, subject, message.</li>
  <li><strong>Resource downloads (ESG report)</strong>: name, email, company.</li>
  <li><strong>Navigation data</strong> (only with your consent): anonymized IP address,
    pages visited, visit duration, browser type, via Google Analytics 4.</li>
</ul>

<h2>3. Purposes of processing</h2>
<ul>
  <li>Respond to your contact and investment opportunity requests.</li>
  <li>Send you the requested resources (reports, white papers).</li>
  <li>Improve site performance and usability (anonymous statistics).</li>
  <li>Comply with our legal and regulatory obligations.</li>
</ul>

<h2>4. Legal basis</h2>
<p>Processing is based on:</p>
<ul>
  <li>Your <strong>explicit consent</strong> (analytics cookies, lead magnet).</li>
  <li>The <strong>legitimate interest</strong> of Richild Invest GBC to respond to your requests.</li>
  <li>The <strong>performance of pre-contractual measures</strong> at your request.</li>
</ul>

<h2>5. Retention periods</h2>
<ul>
  <li>Contact forms: <strong>3 years</strong> from the last exchange.</li>
  <li>Leads (downloads): <strong>3 years</strong> from registration.</li>
  <li>Analytics data (GA4): <strong>14 months</strong> (anonymized).</li>
</ul>

<h2>6. Recipients and processors</h2>
<p>Your data is accessible only to authorized persons within Richild Invest GBC and to our
  technical processors:</p>
<ul>
  <li><strong>MongoDB Atlas</strong> (database hosting, EU - Paris)</li>
  <li><strong>Render.com</strong> (application hosting, EU - Frankfurt)</li>
  <li><strong>Resend</strong> (transactional email service)</li>
  <li><strong>Google Analytics 4</strong> (audience measurement, with consent)</li>
  <li><strong>Calendly</strong> (appointment booking, upon your interaction)</li>
</ul>
<p>No data is sold to third parties for commercial purposes.</p>

<h2>7. Transfers outside the EU</h2>
<p>
  Some processors (Render, Resend, Google) are located outside the European Union. These
  transfers are governed by the <strong>European Commission's Standard Contractual Clauses</strong>
  and/or by recognized certification programs (Data Privacy Framework).
</p>

<h2>8. Your rights</h2>
<p>In accordance with the GDPR, you have the following rights:</p>
<ul>
  <li><strong>Right of access</strong> to your personal data.</li>
  <li><strong>Right to rectification</strong> in case of inaccuracy.</li>
  <li><strong>Right to erasure</strong> ("right to be forgotten").</li>
  <li><strong>Right to restrict</strong> processing.</li>
  <li><strong>Right to data portability</strong>.</li>
  <li><strong>Right to object</strong> to processing.</li>
  <li><strong>Right to withdraw consent</strong> at any time.</li>
</ul>
<p>
  To exercise your rights, contact us at:
  <a href="mailto:info@richildinvest.com">info@richildinvest.com</a>.
  You also have the right to lodge a complaint with the CNIL
  (<a href="https://www.cnil.fr" target="_blank" rel="noopener noreferrer">www.cnil.fr</a>).
</p>

<h2>9. Cookies</h2>
<p>Our site uses only two types of cookies:</p>
<ul>
  <li><strong>Essential cookies</strong> (language preference, GDPR choice): necessary for
    the operation of the site, exempt from consent.</li>
  <li><strong>Analytics cookies (Google Analytics 4)</strong>: set only after your explicit
    consent via the cookie banner. IP addresses anonymized.</li>
</ul>
<p>You can modify your preferences at any time using the button below.</p>

<h2>10. Security</h2>
<p>
  We implement all reasonable technical and organizational measures to protect your data:
  HTTPS encryption (TLS 1.3), strong authentication, restricted access, regular backups.
</p>

<h2>11. Changes</h2>
<p>
  This policy may be modified at any time. The version in force is always the one
  published on this page.
</p>
`;
